import React from 'react';
import SEO from '../seo/SEO';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const PageContainer = styled.section`
  padding: 150px 0 80px; /* account for fixed header */
  background: #ffffff;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 60px;
`;

const HeaderWrap = styled.div`
  text-align: center;
  margin-bottom: 50px;
`;

const Title = styled.h1`
  font-family: 'Tiro Bangla', serif;
  font-size: clamp(28px, 5vw, 42px);
  font-weight: 400;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #67313a;
  margin: 0 0 6px;
`;

const Subtitle = styled.div`
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: #a89b97;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 46px 46px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(motion.figure)`
  margin: 0;
  text-align: center;
`;

const LinkWrap = styled(Link)`
  display: block;
  text-decoration: none;
  color: inherit;
`;

const ImageBox = styled.div`
  width: 100%;
  aspect-ratio: 3 / 4;
  overflow: hidden;
  background: #f2f2f2;
  box-shadow: 0 16px 36px rgba(0,0,0,0.08);
  border-radius: 2px;
  transition: transform 0.35s ease, box-shadow 0.35s ease;
  ${LinkWrap}:hover & {
    transform: translateY(-6px);
    box-shadow: 0 18px 40px rgba(0,0,0,0.12);
  }
`;

const Poster = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.35s ease, filter 0.35s ease;
  display: block;
  ${LinkWrap}:hover & {
    transform: scale(1.03);
  }
`;

const Caption = styled.figcaption`
  margin-top: 14px;
  font-family: 'Montserrat', sans-serif;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  font-size: 12px;
  color: #8f8a88;
`;

const Subcap = styled.div`
  font-family: 'Cormorant Garamond', serif;
  font-style: italic;
  color: #a7a1a0;
  font-size: 14px;
  margin-top: 6px;
`;

// Build SEO-friendly slugs based on the gallery subtitle (venue/location)
const normalize = (s) => String(s)
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '') // strip accents
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_') // non-alnum -> underscore
  .replace(/^_+|_+$/g, ''); // trim underscores

const makeSlug = (sub) => {
  if (!sub) return 'wedding_story';
  const base = normalize(sub);
  return `wedding_${base}`;
};

const makeSlugWithTitle = (sub, title) => `wedding_${normalize(sub)}_${normalize(title)}`;

const items = [
  { slug: 'n1', title: 'VALERIE & KEITH', sub: 'Annecy - Savoie', img: '/images/Wedding Annecy Savoie.webp' },
  { slug: 'n2', title: 'CLAIRE & QUENTIN', sub: 'Entrevaux - Provence', img: '/images/Wedding entrevaux Provence.webp' },
  { slug: 'n3', title: 'ADELIE & JULIEN', sub: 'Menthon-Saint-Bernard - Savoie', img: '/images/wedding Palace de Menthon - Savoie (1).webp' },
  { slug: 'n4', title: 'RAPHAELLE & MATTHIEU', sub: 'Chateau d\'Ailly', img: '/images/Raphaelle_Matthieu/LL3_1750.webp' },
  { slug: 'n6', title: 'MAROUSSIA & CHARLES', sub: 'loft 440', img: '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (9).webp' },
  { slug: 'n5', title: 'TANIA & NICOLAS', sub: 'Domaine des Saints Peres', img: '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (41).webp' },
  { slug: 'n7', title: 'SARAH & NATHANIEL', sub: 'Château de Matel', img: '/images/Sarah_Nathaniel/Luxury wedding photography France  (29).webp' },
  { slug: 'n8', title: 'AURELIE & DAVID', sub: 'Château de Matel', img: '/images/Aurélie_David/French wedding photographer - Lino Ludovic (40).webp' },
  { slug: 'n9', title: 'LEA & AURELIEN', sub: 'Six-Fours-les-Plages - Var', img: '/images/Lea_Aurelien/Wedding - Day after photoshoot - France (7).webp' },
];

const PortfolioPage = () => {
  const { lang } = useLanguage();
  const prefix = lang === 'fr' ? '/fr' : '';
  return (
    <PageContainer>
      <ContentWrapper>
        <SEO
          title={lang === 'fr' ? 'Portfolio Mariage — Lino Ludovic' : 'Wedding Portfolio — Lino Ludovic'}
          description={
            lang === 'fr'
              ? 'Sélection de mariages et éditoriaux — Annecy, Savoie, Provence et au‑delà. Photographie intemporelle et élégante.'
              : 'Selected weddings and editorials — Annecy, Savoie, Provence and beyond. Timeless, elegant photography.'
          }
          image="/images/Wedding Annecy Savoie.jpg"
          type="website"
          schema={{
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: lang === 'fr' ? 'Portfolio Mariage' : 'Wedding Portfolio'
          }}
        />
        <HeaderWrap>
          <Title>Portfolio</Title>
          <Subtitle>{lang === 'fr' ? 'Séance engagement • Mariage • Éditorial' : 'Engagement • Weddings • Editorial'}</Subtitle>
        </HeaderWrap>

        <Grid>
          {(() => {
            // detect duplicate bases to ensure unique URLs per card
            const counts = items.reduce((m, it) => {
              const base = makeSlug(it.sub);
              m[base] = (m[base] || 0) + 1;
              return m;
            }, {});
            return items.map((it, idx) => {
              const base = makeSlug(it.sub);
              const linkSlug = counts[base] > 1 ? makeSlugWithTitle(it.sub, it.title) : base;
            return (
              <Card
                key={it.slug}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.05 * idx }}
              >
                <LinkWrap to={`${prefix}/portfolio/${linkSlug}`}>
                  <ImageBox>
                    <Poster src={it.img} alt={it.title} />
                  </ImageBox>
                  <Caption>{it.title}</Caption>
                  <Subcap>{it.sub}</Subcap>
                </LinkWrap>
              </Card>
            );
            });
          })()}
        </Grid>
      </ContentWrapper>
    </PageContainer>
  );
};

export default PortfolioPage;
